// Helpers for formatting backend errors returned by the policy deploy / activate
// / deactivate endpoints into user-facing strings.
//
// Extracted from `pages/Policies.tsx` to keep that page under the
// page-complexity-guard line budget. Behaviour is intentionally identical to
// the previous in-page helpers; see `policyTroubleshooting.ts` for higher-level
// formatting (reason codes, troubleshooting hints).

/**
 * Format the `detail` payload of a `policy_target_invalid` error into a
 * human-readable sentence. Returns '' when the detail does not match a known
 * shape so callers can fall back to a generic message.
 */
export function formatPolicyTargetDetail(detail: any): string {
  if (!detail) return '';
  const code = String(detail?.code || '').trim();
  const cameraId = String(detail?.camera_id || '').trim();
  const edgeDeviceId = String(detail?.edge_device_id || '').trim();
  const candidates = Array.isArray(detail?.candidate_edge_device_ids)
    ? detail.candidate_edge_device_ids.map((v: any) => String(v || '').trim()).filter(Boolean)
    : [];

  switch (code) {
    case 'policy_target_required':
      return 'Select a camera before activating or deploying this policy.';
    case 'camera_not_found':
      return cameraId
        ? `Camera "${cameraId}" is not registered for this site.`
        : 'The selected camera is not registered for this site.';
    case 'camera_device_ambiguous':
      return candidates.length > 0
        ? `Camera "${cameraId}" exists on multiple devices (${candidates.join(', ')}). Select the correct device.`
        : `Camera "${cameraId}" exists on multiple devices. Select the correct device.`;
    case 'camera_device_mismatch':
      return candidates.length > 0
        ? `Camera "${cameraId}" does not belong to device "${edgeDeviceId}". Try one of: ${candidates.join(', ')}.`
        : `Camera "${cameraId}" does not belong to device "${edgeDeviceId}".`;
    default:
      if (typeof detail?.message === 'string' && detail.message.trim()) return detail.message.trim();
      if (typeof detail === 'string' && detail.trim()) return detail.trim();
      return '';
  }
}

/**
 * Coerce a free-form `detail` field (string | array | object | null) into a
 * displayable string. The backend has historically returned `detail: []` and
 * `detail: { message: "..." }` for the same error shape; the previous in-page
 * helper treated `[]` as truthy and produced an empty snackbar.
 */
export function coerceDetailToString(detail: unknown): string {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail.trim();
  if (Array.isArray(detail)) {
    if (detail.length === 0) return '';
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          const obj = item as Record<string, unknown>;
          const msg = obj.message ?? obj.msg ?? obj.detail ?? obj.reason;
          if (typeof msg === 'string' && msg.trim()) return msg.trim();
        }
        return '';
      })
      .filter(Boolean);
    return parts.join('; ');
  }
  if (typeof detail === 'object') {
    const obj = detail as Record<string, unknown>;
    const msg = obj.message ?? obj.detail ?? obj.reason;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  return '';
}

/**
 * Convert an axios-style error from the policy API into a user-facing string,
 * preferring structured backend codes over raw `err.message`. Always returns a
 * non-empty string (falls back to `fallback`).
 */
export function formatPolicyApiError(err: any, fallback: string): string {
  const rawError = err?.response?.data?.error;
  if (rawError && typeof rawError === 'object') {
    const code = String(rawError?.code || '').trim();
    if (code === 'policy_target_invalid') {
      const msg = String(rawError?.message || '').trim();
      const detailMsg = formatPolicyTargetDetail(rawError?.detail);
      return [msg, detailMsg].filter(Boolean).join(' ').trim() || fallback;
    }
    if (code === 'policy_lint_blocked') {
      const firstIssue = Array.isArray(rawError?.issues)
        ? rawError.issues.map((i: any) => String(i?.message || i?.kind || '').trim()).filter(Boolean)[0]
        : '';
      return (
        [String(rawError?.message || '').trim(), firstIssue]
          .filter(Boolean)
          .join(' ')
          .trim() || fallback
      );
    }
    if (typeof rawError?.message === 'string' && rawError.message.trim()) return rawError.message.trim();
    const errDetail = coerceDetailToString(rawError?.detail);
    if (errDetail) return errDetail;
  }
  const detailFromBody = coerceDetailToString(err?.response?.data?.detail);
  if (detailFromBody) return detailFromBody;
  if (typeof rawError === 'string' && rawError.trim()) return rawError.trim();
  const reasonCode = err?.response?.data?.reason_code;
  if (typeof reasonCode === 'string' && reasonCode.trim()) return reasonCode.trim();
  if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();
  return fallback;
}
